import { Constructor, IDisposable } from '@skylab-shared/types';
import { ListenersRegistry } from '@skylab-shared/utils';
import { v4 as uuid } from 'uuid';
import { ConsoleLogger } from '../../logger';
import { TaskQueue } from '../../task';
import { Weaver } from './../../aop';
import { BusinessObjectBase, InternalBusinessObjectConfig } from './../../bo';
import { Domain, ModelBase, PropertyType } from './../../model';
import { ObservableCollection } from './../../observable';
import { AttachedRule } from './attached-rule';
import { CollectionChangedJoinPoint } from './joinpoints/collection-changed';
import { PropertyChangedJoinPoint } from './joinpoints/property-changed';

export abstract class RulesEngine<TBo extends BusinessObjectBase<TBoModel>, TBoModel extends ModelBase, TRuleResult, TRuleArgs = undefined> extends ListenersRegistry(class { }) implements IDisposable {
    private static logger = new ConsoleLogger(RulesEngine.name);

    private readonly queue: TaskQueue<AttachedRule<TRuleResult, TRuleArgs>>;

    protected readonly weaver: Weaver;
    protected readonly result: ObservableCollection<TRuleResult & RuleResult>;

    protected bo!: TBo;
    protected domain!: Domain;
    protected ruleDefinitions: RuleDefinition<TRuleArgs>[];
    protected attachedRules: AttachedRule<TRuleResult, TRuleArgs>[];

    public constructor(
        weaver: Weaver
    ) {
        super();
        this.weaver = weaver;

        this.queue = new TaskQueue((record, done) => {
            const rules = Array.isArray(record) ? record : [record];
            this.processTask(rules, done);
        }, {
            interval: 25,
            enableBatching: true,
            duplicateDetectionComparer: (recordA, recordB) => recordA.equals(recordB)
        });
        this.ruleDefinitions = [];
        this.attachedRules = [];

        this.result = new ObservableCollection<TRuleResult & RuleResult>();
    }

    //#region Public members

    public attachBusinessObject(bo: TBo): void {
        this.bo = bo;
        this.domain = (<InternalBusinessObjectConfig<ModelBase>>Reflect.getMetadata('skl:bo', bo.constructor)).domain;
    }

    public startProcessing(): void {
        this.attachAllRules();
        this.handleDomainChanges();
        this.handleModelChanges();

        this.queue.startProcessing();
    }

    public queueValidation(): void;
    public queueValidation(model: ModelBase): void;
    public queueValidation<TModel extends ModelBase>(property: PropertyType<TModel>): void;
    public queueValidation<TModel extends ModelBase>(modelOrProperty?: ModelBase | PropertyType<TModel>): void {
        let rules: AttachedRule<TRuleResult, TRuleArgs>[] = [];

        if (modelOrProperty instanceof ModelBase) {
            rules = this.attachedRules.filter(r => r.attachedToTarget(modelOrProperty));
        } else if (modelOrProperty instanceof Array) {
            rules = this.attachedRules.filter(r => r.effectOn(modelOrProperty));
        } else {
            rules = this.attachedRules;
        }

        this.queueAttachedRulesPerformation(rules);
    }

    public addRule<TTriggeredByModel extends ModelBase, TEffectOnModel extends ModelBase>(
        triggeredBy: PropertyType<TTriggeredByModel>[],
        effectOn: PropertyType<TEffectOnModel>[],
        func: RuleFunc<TBo, TBoModel, TTriggeredByModel, TEffectOnModel>,
        args?: TRuleArgs): this;
    public addRule<TModel extends ModelBase>(
        properties: PropertyType<TModel>[],
        func: RuleFunc<TBo, TBoModel, TModel, TModel>,
        args?: TRuleArgs): this;
    public addRule(...args: unknown[]): this {
        if (isArgsWithTriggeredByAndEffectOn(args)) {
            const [properties, ruleFunc, ruleArgs] = args;
            this.prepareAndAddRuleDefinition(
                properties,
                properties,
                {
                    funcAsync: (...funcArgs) => Promise.resolve(ruleFunc(...funcArgs)),
                    func: (...funcArgs) => ruleFunc(...funcArgs) as boolean
                },
                ruleArgs
            );
        }
        if (isArgsWithProperties(args)) {
            const [triggeredBy, effectOn, ruleFunc, ruleArgs] = args;
            this.prepareAndAddRuleDefinition(
                triggeredBy,
                effectOn,
                {
                    funcAsync: (...funcArgs) => Promise.resolve(ruleFunc(...funcArgs)),
                    func: (...funcArgs) => ruleFunc(...funcArgs) as boolean
                },
                ruleArgs
            );
        }
        return this;

        function isArgsWithTriggeredByAndEffectOn(args: any[]): args is [PropertyType<ModelBase>[], RuleFunc<BusinessObjectBase<ModelBase>, ModelBase, ModelBase, ModelBase>, TRuleArgs] {
            return args[0] instanceof Array && typeof args[1] === 'function';
        }

        function isArgsWithProperties(args: any[]): args is [PropertyType<ModelBase>[], PropertyType<ModelBase>[], RuleFunc<BusinessObjectBase<ModelBase>, ModelBase, ModelBase, ModelBase>, TRuleArgs] {
            return args[0] instanceof Array && args[1] instanceof Array && typeof args[2] === 'function';
        }
    }

    public waitAsync(): Promise<void> {
        return this.queue.waitAsync();
    }

    public dispose(): void {
        this.queue.stopProcessing();
        this.deregisterAllListeners();
    }

    //#endregion

    //#region Protected methods

    protected abstract createAttachedRule(rule: RuleDefinition<TRuleArgs>, triggeredByModel: ModelBase, effectOnModel: ModelBase): AttachedRule<TRuleResult, TRuleArgs>;

    protected prepareAndAddRuleDefinition(
        triggeredBy: PropertyType<ModelBase>[],
        effectOn: PropertyType<ModelBase>[],
        rule: Rule,
        args: TRuleArgs
    ): void {
        const ruleDefinition = this.prepareRuleDefinition(triggeredBy, effectOn, rule, args);
        this.ruleDefinitions.push(ruleDefinition);
    }

    protected prepareRuleDefinition(
        triggeredBy: PropertyType<ModelBase>[],
        effectOn: PropertyType<ModelBase>[],
        rule: Rule,
        args: TRuleArgs
    ): RuleDefinition<TRuleArgs> {
        return {
            id: uuid(),
            triggeredBy,
            effectOn,
            rule,
            args
        };
    }

    protected queueAttachedRulesPerformation(attachedRules: AttachedRule<TRuleResult, TRuleArgs>[]): void {
        this.queue.push(...attachedRules);
    }

    //#endregion

    //#region Private methods

    //#region Attaching & dettaching rules

    private attachAllRules(): void {
        const models = this.getAllModels();
        models.forEach(model => this.attachRules(model, models));
    }

    private attachRules(model: ModelBase, allModels: ModelBase[]): void {
        const rules = this.getRuleDefinitionsToAttach(model);
        const rulesToAttach = this.prepareRulesToAttach(rules, model, allModels);
        const notAttachedYet = ([{ id: ruleId }, triggeredBy, effectOn]: [RuleDefinition<TRuleArgs>, ModelBase, ModelBase]) => {
            const rulesAlreadyAttached = this.attachedRules
                .filter(ar => ar.attachedTo(triggeredBy, effectOn) && ar.ruleId === ruleId)
                .map(ar => ar.ruleId);
            return !rulesAlreadyAttached.some(id => id === ruleId);
        };
        rulesToAttach
            .filter(notAttachedYet)
            .forEach(([rule, triggeredBy, effectOn]) => this.attachRule(rule, triggeredBy, effectOn));
    }

    private attachRule(rule: RuleDefinition<TRuleArgs>, triggeredByModel: ModelBase, effectOnModel: ModelBase): void {
        const attachedRule = this.createAttachedRule(rule, triggeredByModel, effectOnModel);
        this.attachedRules.push(attachedRule);
    }

    private dettachRules(model: ModelBase): void {
        const domainModels = this.getAllModels();
        const supposedlyDetachedModels = this.domain.getModels(model).map(([model]) => model);
        const detachedModels = supposedlyDetachedModels.filter(m => domainModels.indexOf(m) === -1);
        const dettachedRules = this.attachedRules.filter(attachedRule =>
            detachedModels.some(curr => attachedRule.attachedToSource(curr) || attachedRule.attachedToTarget(curr))
        );

        this.attachedRules = this.attachedRules.filter(attachedRule =>
            !dettachedRules.includes(attachedRule)
        );

        this.removeResults(dettachedRules);
    }

    //#endregion

    //#region Observe changes

    private handleDomainChanges(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, async ctx => {
            const newValue = ctx.model.get<unknown>(ctx.propertyName);

            if (newValue instanceof ModelBase) {
                this.attachRules(newValue, this.getAllModels());
                this.queueValidation(newValue);
            }

            newValue instanceof ObservableCollection && newValue.filter(item => item instanceof ModelBase).forEach(model => {
                this.attachRules(model, this.getAllModels());
                this.queueValidation(model);
            });

            ctx.oldValue instanceof ModelBase && this.dettachRules(ctx.oldValue);
            ctx.oldValue instanceof ObservableCollection && (newValue as ObservableCollection<ModelBase>).filter(item => item instanceof ModelBase).forEach((model: ModelBase) => this.dettachRules(model));
        }));

        this.registerListening(this.weaver.register(CollectionChangedJoinPoint, async ctx => {
            ctx.newItems.filter(item => item instanceof ModelBase).forEach((model: ModelBase) => {
                this.attachRules(model, this.getAllModels());
                this.queueValidation(model);
            });

            ctx.oldItems.filter(item => item instanceof ModelBase).forEach((model: ModelBase) => this.dettachRules(model));
        }));
    }

    private handleModelChanges(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, ctx => {
            return this.queuePropertyRulesValidationAsync([<Constructor<ModelBase>>ctx.model.constructor, <keyof ModelBase>ctx.propertyName]);
        }));
    }

    //#endregion

    private queuePropertyRulesValidationAsync<TModel extends ModelBase>(property: PropertyType<TModel>): void {
        const rulesToInvoke = this.attachedRules.filter(ar => ar.triggeredBy(property));
        this.queueAttachedRulesPerformation(rulesToInvoke);
    }

    private processTask(rules: AttachedRule<TRuleResult, TRuleArgs>[], done: () => void): void {
        this.performRulesValidationAsync(rules)
            .then(done, done);
    }

    private async performRulesValidationAsync(attachedRules: AttachedRule<TRuleResult, TRuleArgs>[]): Promise<void> {
        this.removeResults(attachedRules);

        const results: (TRuleResult & RuleResult)[] = [];

        for (const rule of attachedRules) {
            const violations = await this.tryInvokeRuleAsync(rule);
            violations.length > 0 && results.push(...violations);
        }

        results.length > 0 && this.result.push(...results);
    }

    private async tryInvokeRuleAsync(attachedRule: AttachedRule<TRuleResult, TRuleArgs>): Promise<(TRuleResult & RuleResult)[]> {
        try {
            return await attachedRule.invoke();
        } catch (error) {
            RulesEngine.logger.error(error);
            return [];
        }
    }

    private getRuleDefinitionsToAttach(model: ModelBase): RuleDefinition<TRuleArgs>[] {
        return this.ruleDefinitions
            .filter(def => [...def.effectOn, ...def.triggeredBy].some(prop => model instanceof prop[0]));
    }

    private prepareRulesToAttach(rules: RuleDefinition<TRuleArgs>[], model: ModelBase, allModels: ModelBase[]): RuleToAttach<TRuleArgs>[] {
        return rules.reduce<RuleToAttach<TRuleArgs>[]>((rulesToAttach, rule) => {
            const [[triggeredByModelType]] = rule.triggeredBy;
            const [[effectOnModelType]] = rule.effectOn;
            if (triggeredByModelType === effectOnModelType) {
                return [...rulesToAttach, [rule, model, model]];
            } else if (<ModelBase>model instanceof triggeredByModelType) {
                const effectOnModels = allModels.filter(model => model instanceof effectOnModelType);
                return [...rulesToAttach, ...effectOnModels.map(effectOnModel => <RuleToAttach<TRuleArgs>>[rule, model, effectOnModel])];
            } else if (<ModelBase>model instanceof effectOnModelType) {
                const triggeredByModels = allModels.filter(model => model instanceof triggeredByModelType);
                return [...rulesToAttach, ...triggeredByModels.map(triggeredByModel => <RuleToAttach<TRuleArgs>>[rule, triggeredByModel, model])];
            }
            return rulesToAttach;
        }, []);
    }

    private removeResults(rules: AttachedRule<TRuleResult, TRuleArgs>[]): void {
        const toRemove = rules
            .reduce((all, r) => [...all, ...this.result.filter(vr => vr.id === r.ruleId && r.attachedToTarget(vr.source))], <(TRuleResult & RuleResult)[]>[]);
        this.result.remove(...toRemove);
    }

    private getAllModels(): ModelBase[] {
        return this.domain.getModels(this.bo.model).map(([model]) => model);
    }

    //#endregion
}

export interface RuleResult {
    readonly id: string;
    readonly source: ModelBase;
    readonly propertyName: string;
}

export interface RuleDefinition<TRuleArgs = undefined> {
    readonly id: string;
    readonly triggeredBy: PropertyType<ModelBase>[];
    readonly effectOn: PropertyType<ModelBase>[];
    readonly rule: Rule;
    readonly args: TRuleArgs;
}

export interface Rule {
    funcAsync: RuleFuncAsync<BusinessObjectBase<ModelBase>, ModelBase, ModelBase>;
    func: RuleFuncSync<BusinessObjectBase<ModelBase>, ModelBase, ModelBase>;
}

export type RuleFuncAsync<TBO extends BusinessObjectBase<TBOModel>,
    TBOModel extends ModelBase,
    TSourceModel extends ModelBase,
    TTargetModel extends ModelBase = ModelBase> = (ctx: RuleContext<TSourceModel, TTargetModel, TBOModel, TBO>, args?: any) => Promise<boolean>;

export type RuleFuncSync<TBO extends BusinessObjectBase<TBOModel>,
    TBOModel extends ModelBase,
    TSourceModel extends ModelBase,
    TTargetModel extends ModelBase = ModelBase> = (ctx: RuleContext<TSourceModel, TTargetModel, TBOModel, TBO>, args?: any) => boolean;

export type RuleFunc<TBO extends BusinessObjectBase<TBOModel>,
    TBOModel extends ModelBase,
    TSourceModel extends ModelBase,
    TTargetModel extends ModelBase = ModelBase> = RuleFuncAsync<TBO, TBOModel, TSourceModel, TTargetModel> | RuleFuncSync<TBO, TBOModel, TSourceModel, TTargetModel>;

export interface RuleContext<TSourceModel extends ModelBase = ModelBase,
    TTargetModel extends ModelBase = ModelBase,
    TBOModel extends ModelBase = ModelBase,
    TBO extends BusinessObjectBase<TBOModel> = BusinessObjectBase<TBOModel>> {
    readonly bo: TBO;
    /**
     * The object whose change triggers rule executing.
     */
    readonly source: TSourceModel;
    /**
     * The object on which result of rule executing will be set.
     */
    readonly target: TTargetModel;
}

type RuleToAttach<TRuleArgs> = [RuleDefinition<TRuleArgs>, ModelBase, ModelBase];
